import React, { useEffect, useState } from 'react';
import { Plus, X, FileSignature, CheckCircle2, Lock, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import { PARTNERSHIP_TYPES } from '@/lib/constants';
import { useAuth } from '@/context/AuthContext';
import { AGREEMENT } from '@/constants/testIds';

/* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the fetch effects. */
export default function Agreements() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(null);
  const [journey, setJourney] = useState(null);

  const [startups, setStartups] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ startup_id:'', counterparty_user_id:'', partnership_type:'mou_30', terms:'' });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/startups/agreements');
      setItems(data);
    } catch (e) {
      // graceful — just leave empty
      setItems([]);
    }
  };
  useEffect(() => { load(); }, []);

  // For founders, also pull their journey to know if they're allowed to create
  useEffect(() => {
    if (user && ['founder', 'startup_member', 'guest'].includes(user.role)) {
      api.get('/founder/journey').then((r) => setJourney(r.data)).catch(() => {});
    }
  }, [user]);

  const isFounder = user && ['founder', 'startup_member', 'guest'].includes(user.role);
  const psyDone = journey?.steps.find((s) => s.id === 'psychometric')?.status === 'completed';
  const ndaSigned = journey?.steps.find((s) => s.id === 'nda_ready')?.status === 'completed';
  const ndaLocked = isFounder && !psyDone;
  const mouLocked = isFounder && !ndaSigned;

  useEffect(() => {
    if (open) {
      api.get('/startups/').then(r => setStartups(r.data));
      api.get('/users/directory').then(r => setUsers(r.data.filter(u => u.user_id !== user?.user_id)));
    }
  }, [open, user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.startup_id || !form.counterparty_user_id) return toast.error('Pick startup + counterparty');
    setBusy(true);
    try {
      await api.post('/startups/agreements', form);
      toast.success('NDA + MOU generated.');
      setOpen(false);
      setForm({ startup_id:'', counterparty_user_id:'', partnership_type:'mou_30', terms:'' });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const sign = async (id, doc) => {
    try {
      await api.post('/startups/agreements/sign', { agreement_id: id, document: doc });
      toast.success(`${doc.toUpperCase()} signed.`);
      load();
      if (view?.agreement_id === id) {
        const r = await api.get('/startups/agreements');
        setView(r.data.find(a => a.agreement_id === id));
      }
    } catch { toast.error('Sign failed'); }
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="ve-eyebrow">NDA & MOU</div>
          <h1 className="display text-3xl md:text-4xl mt-2">Auto-drafted, dual-signed, audit-ready.</h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">Generate an NDA and partnership MOU in seconds — choose your partnership type (MOU 30% is our gold standard).</p>
        </div>
        <button
          onClick={() => {
            if (ndaLocked) {
              toast.error('Locked — complete your founder journey first.');
              return;
            }
            setOpen(true);
          }}
          disabled={ndaLocked}
          data-testid={AGREEMENT.createBtn}
          className={`self-start ${ndaLocked ? 've-btn-secondary opacity-60 cursor-not-allowed' : 've-btn-primary'}`}
        >
          {ndaLocked ? <Lock size={14}/> : <Plus size={14}/>} New agreement
        </button>
      </div>

      {/* Founder gate banner */}
      {isFounder && journey && journey.next_step && ['profile', 'pitch_deck', 'ai_vetting', 'psychometric'].includes(journey.next_step) && (
        <div className="mt-6 ve-card p-5 border-[#8B3FC5] bg-gradient-to-r from-[#FFF8E5] to-white" data-testid="founder-gate-banner">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Lock size={18} className="text-[#5E1F8B]"/>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#5E1F8B] font-bold">Locked</div>
                <div className="display text-lg font-bold mt-1">Complete your founder journey to unlock NDAs.</div>
                <div className="text-xs text-slate-600 mt-1">
                  Progress: {journey.percent}% · Next: <span className="font-bold text-[#5E1F8B]">{(journey.steps.find((s) => s.id === journey.next_step) || {}).label}</span>
                </div>
              </div>
            </div>
            <Link to="/dashboard/founder" className="ve-btn-primary text-xs">
              Open journey <ArrowRight size={12}/>
            </Link>
          </div>
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-5">
        {items.length === 0 && (
          <div className="ve-card p-10 text-center lg:col-span-3">
            <FileSignature size={28} className="mx-auto text-slate-300 mb-3"/>
            <h3 className="display text-xl">No agreements yet</h3>
          </div>
        )}
        {items.map(a => (
          <div key={a.agreement_id} className="ve-card p-6">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-[0.18em] text-slate-500">{a.partnership_type.replace(/_/g,' ')}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded uppercase tracking-[0.15em] ${a.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>{a.status.replace('_',' ')}</span>
            </div>
            <h3 className="display text-lg mt-2">Agreement · {a.agreement_id.slice(-6)}</h3>
            <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
              <div>
                <div className="text-slate-500 uppercase tracking-[0.15em]">NDA</div>
                <div className="font-medium">{a.nda_signed_by?.length || 0}/2 signed</div>
              </div>
              <div>
                <div className="text-slate-500 uppercase tracking-[0.15em]">MOU</div>
                <div className="font-medium">{a.mou_signed_by?.length || 0}/2 signed</div>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <button onClick={() => setView(a)} className="ve-btn-secondary text-xs px-3 py-1.5">Open</button>
              {!a.nda_signed_by?.includes(user?.user_id) && <button data-testid={AGREEMENT.signNDA} onClick={() => sign(a.agreement_id, 'nda')} className="ve-btn-primary text-xs px-3 py-1.5"><CheckCircle2 size={12}/>Sign NDA</button>}
              {!a.mou_signed_by?.includes(user?.user_id) && <button data-testid={AGREEMENT.signMOU} onClick={() => sign(a.agreement_id, 'mou')} className="ve-btn-primary text-xs px-3 py-1.5"><CheckCircle2 size={12}/>Sign MOU</button>}
            </div>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={(e) => e.target === e.currentTarget && setOpen(false)}>
          <form onSubmit={submit} className="ve-card p-7 w-full max-w-2xl bg-white">
            <div className="flex items-center justify-between">
              <h2 className="display text-2xl">New NDA + MOU</h2>
              <button type="button" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={18}/></button>
            </div>
            <div className="mt-6 space-y-4">
              <label className="block text-xs uppercase tracking-[0.18em] text-slate-500">Startup
                <select required value={form.startup_id} onChange={e => setForm({...form, startup_id:e.target.value})} className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20 normal-case tracking-normal">
                  <option value="">Choose…</option>
                  {startups.map(s => <option key={s.startup_id} value={s.startup_id}>{s.name}</option>)}
                </select>
              </label>
              <label className="block text-xs uppercase tracking-[0.18em] text-slate-500">Counterparty
                <select required value={form.counterparty_user_id} onChange={e => setForm({...form, counterparty_user_id:e.target.value})} className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20 normal-case tracking-normal">
                  <option value="">Choose…</option>
                  {users.map(u => <option key={u.user_id} value={u.user_id}>{u.name} · {u.role}</option>)}
                </select>
              </label>
              <label className="block text-xs uppercase tracking-[0.18em] text-slate-500">Partnership type
                <select data-testid={AGREEMENT.partnerSelect} value={form.partnership_type} onChange={e => setForm({...form, partnership_type:e.target.value})} className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20 normal-case tracking-normal">
                  {PARTNERSHIP_TYPES.map(p => {
                    const isMou = p.id.startsWith('mou');
                    return <option key={p.id} value={p.id} disabled={mouLocked && isMou}>
                      {p.label}{mouLocked && isMou ? ' — locked (sign NDA first)' : ''}
                    </option>;
                  })}
                </select>
              </label>
              <label className="block text-xs uppercase tracking-[0.18em] text-slate-500">Custom terms (optional)
                <textarea rows={3} value={form.terms} onChange={e => setForm({...form, terms:e.target.value})} className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20 normal-case tracking-normal"/>
              </label>
            </div>
            <div className="mt-7 flex justify-end gap-3">
              <button type="button" onClick={() => setOpen(false)} className="ve-btn-secondary">Cancel</button>
              <button type="submit" disabled={busy} className="ve-btn-primary">{busy ? 'Generating…' : 'Generate NDA + MOU'}</button>
            </div>
          </form>
        </div>
      )}

      {view && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={(e) => e.target === e.currentTarget && setView(null)}>
          <div className="ve-card p-7 w-full max-w-4xl bg-white max-h-[88vh] overflow-y-auto">
            <div className="flex items-center justify-between sticky top-0 bg-white pb-4 border-b border-slate-200">
              <h2 className="display text-2xl">Agreement · {view.agreement_id.slice(-8)}</h2>
              <button onClick={() => setView(null)} className="text-slate-400 hover:text-slate-700"><X size={18}/></button>
            </div>
            <section className="mt-6">
              <div className="ve-eyebrow">NDA</div>
              <pre className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-slate-800 font-sans">{view.nda_text}</pre>
            </section>
            <section className="mt-8">
              <div className="ve-eyebrow">MOU</div>
              <pre className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-slate-800 font-sans">{view.mou_text}</pre>
            </section>
          </div>
        </div>
      )}
    </div>
  );
}
